import { useEffect, useState } from "react";
import { invalidate } from "../data/useFetch.js";
import { useSources } from "../data/usePage.js";

function timeAgo(d: Date): string {
  const ms = Date.now() - d.getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Cross-page data control: shows when the page's queries were last refreshed
 * and a single ↻ that re-runs every query embed at once. There's no per-embed
 * refresh — by design, you refresh all together.
 */
export function DataCard() {
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [, setTick] = useState(0);
  const sources = useSources();

  // Keep the "refreshed Xs ago" label live without forcing the parent to
  // re-render: tick once a second only while a timestamp is set.
  useEffect(() => {
    if (!lastRefresh) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [lastRefresh]);

  const onRefresh = (): void => {
    invalidate("query:");
    invalidate("sources"); // re-ping UP/DOWN — the recovery path back to UP
    setLastRefresh(new Date());
  };

  return (
    <section className="dw-card dw-data-card">
      <header className="dw-card-head">data</header>
      <div className="dw-data-body">
        <span className="dw-muted dw-data-status">
          {lastRefresh ? `refreshed ${timeAgo(lastRefresh)}` : "showing cached results"}
        </span>
        <button
          type="button"
          className="dw-data-refresh"
          onClick={onRefresh}
          title="Re-run all queries on this page"
        >
          ↻
        </button>
      </div>
      {sources.status === "ready" && sources.data.length > 0 && (
        <ul className="dw-source-list">
          {sources.data.map((s) => (
            <li key={s.id} className="dw-source-row" title={s.error ?? ""}>
              <span className={`dw-source-dot ${s.up ? "up" : "down"}`} />
              <span className="dw-source-label">{s.label}</span>
              <span className="dw-source-state">{s.up ? "up" : "down"}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
